import { useId } from "react";
import "./ProjectSelectionIcon.scss";

/** Project folder variants for comparing flat, recessed and glossy appearances. */
export function ProjectSelectionIcon({ logo, variant = "inset", logoScale = 1, logoOffsetY = 0 }: {
  logo: string;
  variant?: "outline" | "glossy" | "inset";
  logoScale?: number;
  logoOffsetY?: number;
}) {
  const id = `project-folder-${useId().replace(/:/g, "")}`;
  const folderPath = "M4 29V9Q4 4 9 4H33Q36 4 39 8L44 14Q46 16 50 16H89Q96 16 96 23V72Q96 79 89 79H11Q4 79 4 72Z";
  if (variant === "inset") {
    return (
      <svg className="projectSelectionIcon" viewBox="0 0 100 84" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id={`${id}-recessEdge`} x2="0" y2="1">
            <stop stopColor="#7e858e" />
            <stop offset="0.5" stopColor="#949aa2" />
            <stop offset="1" stopColor="#a9afb5" />
          </linearGradient>
          <filter id={`${id}-inset`} x="-15%" y="-15%" width="130%" height="130%">
            <feComponentTransfer in="SourceAlpha" result="outside">
              <feFuncA type="table" tableValues="1 0" />
            </feComponentTransfer>
            <feGaussianBlur in="outside" stdDeviation="1.8" result="softOutside" />
            <feOffset in="softOutside" dx="2.2" dy="2.9" result="offsetOutside" />
            <feComposite in="offsetOutside" in2="SourceAlpha" operator="in" result="innerEdge" />
            <feFlood floodColor="#3e4752" floodOpacity="0.48" result="shadowColor" />
            <feComposite in="shadowColor" in2="innerEdge" operator="in" result="innerShadow" />
            <feMerge>
              <feMergeNode
                in="SourceGraphic" // no itx
              />
              <feMergeNode
                in="innerShadow" // no itx
              />
            </feMerge>
          </filter>
        </defs>
        <path
          d={folderPath}
          transform="translate(0.85 1.45)" // no itx
          fill="none"
          stroke="#ffffff"
          strokeWidth="3"
          opacity="0.95"
        />
        <path
          d={folderPath}
          fill="#ffffff"
          stroke={`url(#${id}-recessEdge)`}
          strokeWidth="1.65"
          strokeLinejoin="round"
          filter={`url(#${id}-inset)`}
        />
        <image
          href={logo}
          x={50 - 36 * logoScale}
          y={43 - 31 * logoScale + logoOffsetY}
          width={72 * logoScale}
          height={62 * logoScale}
          preserveAspectRatio="xMidYMid meet"
        />
      </svg>
    );
  }
  if (variant === "outline") {
    return (
      <svg className="projectSelectionIcon" viewBox="0 0 100 84" aria-hidden="true" focusable="false">
        <path
          d={folderPath}
          fill="none"
          stroke="#000000"
          strokeWidth="2.2"
          strokeLinejoin="round"
        />
        <image href={logo} x="22" y="26" width="56" height="47" preserveAspectRatio="xMidYMid meet" />
      </svg>
    );
  }
  return (
    <svg className="projectSelectionIcon" viewBox="0 0 100 84" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={`${id}-back`} x2="0" y2="1">
          <stop stopColor="#ffb52e" />
          <stop offset="0.3" stopColor="#f58d04" />
          <stop offset="1" stopColor="#e57e04" />
        </linearGradient>
        <radialGradient id={`${id}-inside`} cx="0.35" cy="0.2" r="1">
          <stop stopColor="#ffffff" />
          <stop offset="0.65" stopColor="#faf8f4" />
          <stop offset="1" stopColor="#eeebe5" />
        </radialGradient>
        <filter id={`${id}-shadow`} x="-20%" y="-20%" width="140%" height="150%">
          <feDropShadow dx="0" dy="2.5" stdDeviation="2" floodColor="#403b33" floodOpacity="0.24" />
        </filter>
        <linearGradient id={`${id}-edge`} x2="0" y2="1">
          <stop stopColor="#ffffff" />
          <stop offset="1" stopColor="#dcd6ca" />
        </linearGradient>
      </defs>
      <path d="M8 31V14Q8 6 16 6H32Q37 6 40 11L43 14Q46 17 51 17H84Q92 17 92 25V63H8Z"
        fill={`url(#${id}-back)`} />
      <path d="M9 15Q9 7 17 7H32Q36 7 39 11" fill="none" stroke="#ffd881" strokeWidth="0.8" opacity="0.7" />
      <path d="M8 31Q8 18 21 18H35Q40 18 44 21H79Q92 21 92 33V65Q92 76 81 76H20Q8 76 8 64Z"
        fill={`url(#${id}-inside)`} stroke={`url(#${id}-edge)`} strokeWidth="0.9"
        filter={`url(#${id}-shadow)`} />
      <image href={logo} x="22" y="26" width="56" height="47" preserveAspectRatio="xMidYMid meet" />
    </svg>
  );
}
